/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import React, { FC, useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { USER_AVATAR_PATH_S3 } from 'constants/images'
import { findAllAdmissionExpert_findAllAdmissionExpert_items as AdmissionExpert } from '__generatedTypes__/findAllAdmissionExpert'
import { UpdateAdmissionExpertInput } from '__generatedTypes__/globalTypes'
import AvatarInput from 'components/AvatarInput'
import { getFileNameFromImagekitLink } from 'utils/getFileNameFromImagekitLink'
import { useImageUpload } from 'utils/saveImageS3'
import FormButtons from 'components/FormButtons'
import * as classes from 'pagesNew/TeamMembers/ManageTeamMemberModal/styles'
import { validateRequired } from 'utils/validation'
import { InputField } from 'components/Input'

interface IProps {
  onClose: () => void
  selectedAdmissionExpert: AdmissionExpert | null
  onAdmissionExpertSubmit: (teamMemberProps: UpdateAdmissionExpertInput) => void
  onAdmissionExpertRemove?: (selectedAdmissionExpertId: string) => void
  removeLoading: boolean
}

export const ManageAdmissionExpertModal: FC<IProps> = ({
  onClose,
  selectedAdmissionExpert,
  onAdmissionExpertRemove,
  onAdmissionExpertSubmit,
  removeLoading,
}) => {
  const isEditMode = !!selectedAdmissionExpert
  const [isImageChanged, setIsImageChanged] = useState<boolean>(false)
  const [imageError, setImageError] = useState<boolean>(false)
  const [selectedImage, setSelectedImage] = useState<File | null>()
  const imageUpload = useImageUpload()

  useEffect(() => {
    ;(async () => {
      const link = selectedAdmissionExpert?.getAvatar.photo
      if (link) {
        const filename = getFileNameFromImagekitLink(link)
        if (link) {
          const response = await fetch(link)
          const blob = await response.blob()
          const file = new File([blob], filename)
          if (file) {
            setSelectedImage(file)
          }
        }
      }
    })()
  }, [selectedAdmissionExpert?.getAvatar?.photo])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = async (fieldValues: UpdateAdmissionExpertInput) => {
    if (selectedImage) {
      setImageError(false)
      const expertUrl = isImageChanged ? await imageUpload(selectedImage, USER_AVATAR_PATH_S3) : null
      const expertProps = {
        degree: fieldValues.degree,
        description: fieldValues.description,
        name: fieldValues.name,
        avatar: expertUrl ?? fieldValues.avatar,
        email: fieldValues.email,
      }
      onAdmissionExpertSubmit(expertProps)
      onClose()
    } else {
      setImageError(true)
    }
  }

  const handleRemove = async () => {
    if (onAdmissionExpertRemove && selectedAdmissionExpert) {
      onAdmissionExpertRemove(selectedAdmissionExpert?.id)
    }
  }

  return (
    <div css={classes.modalContainer}>
      <Form
        onSubmit={handleSubmit}
        initialValues={selectedAdmissionExpert}
        render={({ handleSubmit, submitting }) => (
          <form css={classes.modalForm} onSubmit={handleSubmit}>
            <Typography css={classes.modalTitle} variant="title2">
              {isEditMode ? 'Edit' : 'Create'} Admission Expert
            </Typography>
            <AvatarInput
              isEditMode
              imageError={imageError}
              setImageError={setImageError}
              selectedImage={selectedImage}
              setSelectedImage={(value) => {
                setSelectedImage(value)
                setIsImageChanged(true)
              }}
            />
            <InputField name="name" validate={validateRequired} label="Name" placeholder="Enter Name" fullWidth />
            <InputField name="degree" validate={validateRequired} label="Degree" placeholder="Enter Degree" fullWidth />
            <InputField
              name="description"
              validate={validateRequired}
              label="Description"
              placeholder="Enter Description"
              fullWidth
              multiline
            />
            <InputField name="email" validate={validateRequired} label="Email" placeholder="Enter Email" fullWidth />
            <FormButtons
              onDelete={handleRemove}
              onClose={onClose}
              removeLoading={removeLoading}
              submitting={submitting}
              isEditMode={isEditMode}
            />
          </form>
        )}
      />
    </div>
  )
}
